import { useEffect, useState } from 'react';
import { readString, writeString } from '../../utils/guarded-storage';
import { isBackNavigation } from '../../utils/navigation-direction';

/** sessionStorage prefix: how many tiles the grid at a URL last showed. */
export const GRID_TILES_KEY = 'grid_tiles_';

/** Stop holding after this long even if the list never fills: a skeleton forever is worse. */
export const GRID_HOLD_MAX_MS = 8000;

/** A count past this is not a real grid; ignore it. */
const MAX_TILES = 2000;

function keyForThisUrl(): string {
  return `${GRID_TILES_KEY}${window.location.pathname}${window.location.search}`;
}

/**
 * How many skeleton tiles to add after the grid so that, on Back, the page is
 * as tall as when the reader left it.
 *
 * Owner, 2026-10-20: "we keep getting the footer". Back restores the scroll
 * position before the listings are on screen. With only the 12-tile first
 * screen skeleton the page was a screen tall, so the browser and
 * useScrollRestoration clamped to the bottom: the footer. Holding the grid at
 * its old tile count from the first frame gives the scroll something to land
 * on, and the real tiles take the skeletons' places one for one (same
 * `.listing` box).
 *
 * The count comes from this tab's sessionStorage, written as the grid grows.
 * Forward visits hold nothing. The hold ends when the list is as long as it
 * was, when paging is done, or after GRID_HOLD_MAX_MS.
 */
export function useGridHoldOnBack(listLength: number, done: boolean): number {
  const [target, setTarget] = useState<number>(() => {
    if (typeof window === 'undefined' || !isBackNavigation()) return 0;
    const saved = Number(readString('session', keyForThisUrl()));
    return Number.isFinite(saved) && saved > 0 ? Math.min(saved, MAX_TILES) : 0;
  });

  useEffect(() => {
    if (listLength > 0) writeString('session', keyForThisUrl(), String(listLength));
  }, [listLength]);

  useEffect(() => {
    if (target === 0) return undefined;
    const timer = setTimeout(() => setTarget(0), GRID_HOLD_MAX_MS);
    return () => clearTimeout(timer);
  }, [target]);

  if (done) return 0;
  return Math.max(0, target - listLength);
}

export default useGridHoldOnBack;
